<template>
  <div class="zoom-button-wrapper">
    <button
      @click="zoomOpen"
      class="font-icon-zoom-icon"
      :class="buttonClasses"
      :aria-label="ariaLabelZoom"
    ></button>
    <div
      v-if="active"
      :class="zoomControlsClasses"
      :aria-expanded="ariaExpanded"
    >
      <div class="zoom-item" aria-live="assertive" :class="zoomValueClasses">
        {{ zoomValue }}%
      </div>
      <div class="zoom-item zoom-out">
        <button
          class="zoom-out-button font-icon-zoom-out-icon"
          :class="zoomControlsIconClasses"
          tabindex="0"
          :aria-label="ariaLabelZoomOut"
          @click="zoomOut"
        ></button>
      </div>
      <div class="zoom-item zoom-in">
        <button
          class="zoom-in-button font-icon-zoom-in-icon"
          :class="zoomControlsIconClasses"
          tabindex="0"
          :aria-label="ariaLabelZoomIn"
          @click="zoomIn"
        ></button>
      </div>
      <button
        class="zoom-item"
        tabindex="0"
        :aria-label="ariaLabelZoomReset"
        :class="zoomResetClasses"
        @click="zoomReset"
      >
        {{ ariaLabelZoomReset }}
      </button>
    </div>
  </div>
</template>

<script>
import smoothscroll from 'smoothscroll-polyfill';

// kick off the polyfill!
smoothscroll.polyfill();

export default {
  name: 'ZoomButton',
  data() {
    return {
      active: false,
      zoomValue: 100,
    };
  },
  props: {
    customClass: {
      type: String,
      default: '',
    },
    buttonIcon: {
      type: String,
      default: 'plus-round-2',
    },
    bgColorZoomButton: {
      type: String,
      required: false,
    },
    bgColorControls: {
      type: String,
      required: false,
      default: null,
    },
    zoomValueColor: {
      type: String,
      required: false,
      default: null,
    },
    zoomResetColor: {
      type: String,
      required: false,
      default: null,
    },
    zoomIconColorDefault: {
      type: String,
      required: false,
      default: null,
    },
    zoomIconColorHover: {
      type: String,
      required: false,
      default: null,
    },
    zoomIconColorActive: {
      type: String,
      required: false,
      default: null,
    },
    controlsIconColorDefault: {
      type: String,
      required: false,
      default: null,
    },
    controlsIconColorHover: {
      type: String,
      required: false,
      default: null,
    },
    ariaLabelZoom: {
      type: String,
      default: 'Zoom icon',
    },
    ariaLabelZoomOut: {
      type: String,
      default: 'Zoom out',
    },
    ariaLabelZoomIn: {
      type: String,
      default: 'Zoom in',
    },
    ariaLabelZoomReset: {
      type: String,
      default: 'Reset',
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.zoomValue = this.getZoom;
    });
  },
  methods: {
    zoomOpen() {
      this.active = !this.active;
    },
    zoomIn() {
      if (this.zoomValue < 200) {
        if (this.zoomValue === 100) {
          this.zoomValue = 110;
        } else if (this.zoomValue === 110) {
          this.zoomValue = 125;
        } else {
          this.zoomValue += 25;
        }
      }
    },
    zoomOut() {
      if (this.zoomValue > 100) {
        if (this.zoomValue === 125) {
          this.zoomValue = 110;
        } else if (this.zoomValue === 110) {
          this.zoomValue = 100;
        } else {
          this.zoomValue -= 25;
        }
      }
    },
    zoomReset() {
      if (this.zoomValue !== 100) {
        this.zoomValue = 100;

        this.$nextTick(() => {
          window.scroll({ top: 0, left: 0, behavior: 'smooth' });
        });
      }
    },
  },
  computed: {
    ariaExpanded() {
      return this.active ? 'true' : 'false';
    },
    buttonClasses() {
      const zoomClass = {
        'zoom-button': true,
        'zoom-main-icon': true,
        'active': !!this.active,
      };

      zoomClass['zoom-button-active'] = this.active;
      zoomClass['zoom-button-inactive'] = !this.active;
      zoomClass[`bg-${this.bgColorZoomButton}-active`] = this.active;
      zoomClass[`icon-font-color-${this.zoomIconColorDefault}`] =
        !!this.zoomIconColorDefault;
      zoomClass[`icon-font-color-${this.zoomIconColorHover}-hover-state`] =
        !!this.zoomIconColorHover;
      zoomClass[`icon-font-color-${this.zoomIconColorActive}-active-state`] =
        !!this.zoomIconColorActive;

      return zoomClass;
    },
    zoomControlsClasses() {
      const zoomControlsClasses = {
        'zoom-controls': true,
      };

      zoomControlsClasses[`bg-${this.bgColorControls}`] =
        !!this.bgColorControls;

      return zoomControlsClasses;
    },
    zoomValueClasses() {
      const zoomValueClasses = {
        'zoom-value': true,
      };

      zoomValueClasses[`text-${this.zoomValueColor}`] = !!this.zoomValueColor;

      return zoomValueClasses;
    },
    zoomResetClasses() {
      const zoomResetClasses = {
        'zoom-reset': true,
      };

      zoomResetClasses[`text-${this.zoomResetColor}`] = !!this.zoomResetColor;

      return zoomResetClasses;
    },
    zoomControlsIconClasses() {
      const zoomIconClasses = {
        'zoom-controls-icon': true,
      };

      zoomIconClasses[`icon-font-color-${this.controlsIconColorDefault}`] =
        !!this.controlsIconColorDefault;
      zoomIconClasses[
        `icon-font-color-${this.controlsIconColorHover}-hover-state`
      ] = !!this.controlsIconColorHover;

      return zoomIconClasses;
    },
  },
  watch: {
    zoomValue: {
      handler(value) {
        this.setZoom(value);
      },
      immediate: true,
    },
  },
  destroyed() {
    this.resetZoom();
    window.scroll({ top: 0, left: 0, behavior: 'smooth' });
  },
};
</script>

<style lang="stylus" scoped>
@import '../styles/zoom_button.styl';
</style>
